import { Flex, Form, Input, Button } from 'antd';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormSchema } from '../hooks/use-form-schema';
import type { OrganizationSmallFormValues } from '../lib/types';
import { authActions } from '@shared/redux/ducks/auth';
import { useAppDispatch } from '@shared/redux';
import styles from './organization-small.module.scss';
import { PlusBackground } from '@shared/assets/icons/components';
import { useCreateOrganization } from '../../organization-create-form/hooks/use-create-organization';
import { router } from '@app/providers/router/router-provider';
import { Routes } from '@shared/constants/routes';

export const OrganizationSmallForm = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'profile.organization.formCreate' });
  const dispatch = useAppDispatch();

  const organizationSmallFormSchema = useFormSchema();

  const {
    handleSubmit,
    control,
    clearErrors,
    formState: { errors, isSubmitting, isValid },
  } = useForm<OrganizationSmallFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(organizationSmallFormSchema),
    defaultValues: {
      title: '',
    },
  });

  const handleCachedOrganizationName = (name: string) => {
    dispatch(authActions.setCurrentOrganizationName(name));
  };

  const { createOrganization, isLoading } = useCreateOrganization();

  const handleCreateOrganization = (values: OrganizationSmallFormValues) => {
    createOrganization({ title: values.title }).then(() => {
      router.navigate(Routes.analytics.root);
    });
  };

  return (
    <Flex>
      <Form
        onChange={() => clearErrors('root')}
        layout="vertical"
        className={styles.root}
        onFinish={handleSubmit(handleCreateOrganization)}
      >
        <Flex vertical className={styles.container}>
          <Form.Item
            name="title"
            label={t('title')}
            help={errors.title && errors.title.message}
            validateStatus={errors.title ? 'error' : ''}
          >
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <Input
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    handleCachedOrganizationName(e.target.value);
                  }}
                  disabled={isSubmitting}
                  placeholder={t('titlePlaceholder')}
                  className={styles.input}
                />
              )}
            />
          </Form.Item>

          <Form.Item>
            <Button
              block
              type="primary"
              htmlType="submit"
              loading={isSubmitting || isLoading}
              disabled={!isValid || isSubmitting || isLoading}
              icon={<PlusBackground />}
              iconPosition="end"
            >
              {t('submitSmall')}
            </Button>
          </Form.Item>
        </Flex>
      </Form>
    </Flex>
  );
};
